import { Injectable } from '@angular/core';
import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpErrorResponse,
} from '@angular/common/http';
import { Observable, from, throwError, switchMap, catchError } from 'rxjs';
import { Router } from '@angular/router';

import { AuthenticationService } from '../services/modules/core/authentication/authentication-service.service';
import { SessionService } from '../services/shared/session/session.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    /** URLs that should NOT trigger session-expired dialog on 401 */
    private readonly excludedUrls = ['/api/v2/auth/login', '/api/v2/auth/register', '/api/v2/auth/refresh-token'];

    constructor(
        private authenticationService: AuthenticationService,
        private sessionService: SessionService,
        private router: Router
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const authedReq = this.addToken(req);

        return next.handle(authedReq).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401 && this.isOnAdminArea() && !this.isExcluded(req.url)) {
                    return this.handle401(req, next);
                }
                return throwError(() => error);
            })
        );
    }

    private addToken(req: HttpRequest<any>): HttpRequest<any> {
        const session = this.authenticationService.getUserSession();
        if (session?.token) {
            return req.clone({
                setHeaders: { Authorization: `Bearer ${session.token}` },
            });
        }
        return req;
    }

    private handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const result = this.sessionService.checkSession();

        if (result === 'valid') {
            // Token valid client-side but server rejected — something else is wrong
            return throwError(() => new HttpErrorResponse({ status: 401 }));
        }

        return from(result).pipe(
            switchMap(success => {
                if (success) {
                    // Session restored — retry the failed request with the new token
                    const retryReq = this.addToken(req);
                    return next.handle(retryReq);
                }
                return throwError(() => new HttpErrorResponse({ status: 401 }));
            })
        );
    }

    private isOnAdminArea(): boolean {
        return this.router.url.startsWith('/admin-v2') || this.router.url.startsWith('/admin-v3');
    }

    private isExcluded(url: string): boolean {
        return this.excludedUrls.some(excluded => url.includes(excluded));
    }
}
